import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useState } from 'react';
import { useUser } from '../context/UserContext';

interface NavbarProps {
  userName: string;
  displayName?: string;
}

// Add SVG game controller icon
const GameControllerIcon = () => (
  <svg 
    className="w-6 h-6" 
    fill="currentColor" 
    viewBox="0 0 24 24"
  >
    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
  </svg>
);

// Add hamburger menu icon
const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-16 6h16" />
  </svg>
);

const Navbar = ({ userName, displayName }: NavbarProps) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { stats } = useUser();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <nav className="bg-slate-800 shadow-lg p-4 mb-8 border-b border-indigo-500">
      <div className="max-w-7xl mx-auto">
        {/* Desktop and Mobile Header */}
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold text-gray-300 truncate max-w-[200px] sm:max-w-none">
            Welcome, <span className="text-yellow-100">{displayName || userName}</span>!
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-300 hover:text-yellow-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <MenuIcon />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/dashboard" 
              className="text-gray-300 hover:text-yellow-100 transition-colors flex items-center gap-2"
              title="View Available Games"
            >
              <GameControllerIcon />
              <span>Games</span>
            </Link>
            <Link 
              to="/leaderboard"
              className="text-gray-300 hover:text-yellow-100 transition-colors"
              title="View Leaderboards"
            >
              Leaderboard
            </Link>
            <Link 
              to="/profile" 
              className="text-white hover:text-yellow-100 transition-colors"
              title="View Your Profile"
            >
              Profile
            </Link>
            {stats?.isAdmin && (
              <Link 
                to="/admin"
                className="text-gray-300 hover:text-yellow-100 transition-colors"
                title="Admin Dashboard"
              >
                Admin
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="bg-red-500 px-6 py-3 rounded-full hover:bg-red-600 transition-all transform hover:scale-105 font-bold"
              title="Sign Out"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden mt-4 space-y-4`}>
          <Link 
            to="/dashboard" 
            className="text-gray-300 hover:text-yellow-100 flex items-center gap-2 py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            <GameControllerIcon />
            <span>Games</span>
          </Link>
          <Link 
            to="/leaderboard"
            className="text-gray-300 hover:text-yellow-100 block py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            Leaderboard
          </Link>
          <Link 
            to="/profile" 
            className="text-white hover:text-yellow-100 block py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            Profile
          </Link>
          {stats?.isAdmin && (
            <Link 
              to="/admin"
              className="text-gray-300 hover:text-yellow-100 block py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Admin
            </Link>
          )}
          <button
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className="bg-red-500 w-full px-6 py-3 rounded-full hover:bg-red-600 transition-all font-bold"
          >
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 